import { ElementType, ReactNode } from 'react';
import { cn } from '../../utils';
import SecondarySideNav, {
    SideNavMenu,
} from '../SideNav/SecondarySideNav/SecondarySideNav';
import PageHeader, { PageHeaderProps } from '../PageHeader/PageHeader';
import useScrollInfo from '../../hooks/useElementScroll';
import { Prettify } from '@types';

export type FullAlli360LayoutProps =
    | Prettify<Alli360LayoutProps & HasPageHeader>
    | Prettify<Alli360LayoutProps & NoPageHeader>;

type Alli360LayoutProps = {
    menuActiveItems?: string[];
    noPageHeader?: boolean;
    children: ReactNode;
    secondarySideNavMenu?: SideNavMenu[];
    bottomMenu?: SideNavMenu[];
    openMenus?: string[];
    fixedPageHeader?: boolean;
    LinkAs?: ElementType;
    onAccordionTriggerClicked?: (id: string) => void;
    fullHeight?: boolean;
    className?: string;
};

type NoPageHeader = {
    title?: never;
    breadcrumbs?: never;
    subtitle?: never;
    actions?: never;
    backButton?: never;
};

type HasPageHeader = PageHeaderProps;

export default function Alli360Layout({
    title = '',
    children,
    breadcrumbs,
    subtitle = [],
    actions = [],
    fixedPageHeader = false,
    secondarySideNavMenu = [],
    noPageHeader = false,
    openMenus = [],
    bottomMenu = [],
    menuActiveItems = [],
    LinkAs,
    onAccordionTriggerClicked,
    fullHeight = false,
    backButton = false,
    className: userClassName,
    ...rest
}: FullAlli360LayoutProps) {
    // @ts-expect-error This should really never be used, it's just a way to turn off some styles in Storybook
    const __storybook = rest?.__storybook || undefined;
    const { scroll, setRef, ref: _ref, posAtScrollChange } = useScrollInfo(100);

    return (
        <div
            id="containerClasses"
            className={cn(
                'alli-z-20 alli-w-full alli-bg-blue-gray-100',
                userClassName,
            )}
            {...rest}>
            <div
                className={cn('alli-flex', {
                    'alli-h-[calc(100vh_-_60px)]': !__storybook,
                    'alli-h-screen': __storybook,
                })}>
                {secondarySideNavMenu && secondarySideNavMenu.length > 0 && (
                    <SecondarySideNav
                        menuActiveItems={menuActiveItems}
                        bottomMenu={bottomMenu}
                        openMenus={openMenus}
                        menu={secondarySideNavMenu}
                        LinkAs={LinkAs}
                        onAccordionTriggerClicked={onAccordionTriggerClicked}
                    />
                )}
                <div className="brand-gradient alli-absolute alli-top-0 alli-z-0 alli-h-[500px] alli-w-full"></div>
                <div
                    data-testid="ContainerWrapper"
                    className="alli-relative alli-z-10 alli-w-full alli-overflow-y-scroll"
                    ref={setRef}>
                    <div
                        data-testid="InnerContainer"
                        className={cn('alli-relative alli-z-20', {
                            'alli-h-full': fullHeight,
                        })}>
                        {!noPageHeader && (
                            <div
                                data-testid="PageHeaderWrapper"
                                className={cn(
                                    'alli-relative alli-z-20 alli-w-full alli-border-b alli-border-gray-400 alli-bg-white alli-px-6 alli-py-4 alli-transition-all alli-duration-1000 alli-ease-in-out',
                                    {
                                        'alli-sticky alli-top-0':
                                            fixedPageHeader,
                                    },
                                    !fixedPageHeader && {
                                        'alli-relative': scroll.y.value < 100,
                                        'alli-sticky alli-top-0':
                                            scroll.y.direction === -1 &&
                                            posAtScrollChange - scroll.y.value >
                                                100 &&
                                            scroll.y.value > 100,
                                        'alli-sticky -alli-top-40':
                                            (scroll.y.direction === -1 &&
                                                posAtScrollChange -
                                                    scroll.y.value <
                                                    100) ||
                                            (scroll.y.direction === 1 &&
                                                scroll.y.value -
                                                    posAtScrollChange <
                                                    100),
                                    },
                                )}>
                                <PageHeader
                                    breadcrumbs={breadcrumbs}
                                    subtitle={subtitle}
                                    actions={actions}
                                    title={title}
                                    backButton={backButton}
                                />
                            </div>
                        )}

                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
